import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormSpy } from 'react-final-form';
import cn from 'classnames';
import utils from 'utils';
import { ANALYTICS } from 'constants';
import VisHiddenText from 'components/VisHiddenText';

/**
 * PasswordRequirements:
 * A formspy component that must be placed inside of a FinalForm component
 * it validates the password field in comparison to the other 3 fields
 * it displays our password requirements and only shows X icons in certain situations
 *
 * @param {string} password - name of the password field
 * @param {string} firstName - name of the firstName field
 * @param {string} lastName - name of the lastName field
 * @param {string} email - name of the email field
 * @param {function(valid: boolean)} setPasswordValid - setter for password requirements validity on the parent form
 *
 * @return {JSX} ...
 */

const PasswordRequirements = ({ password, firstName, lastName, email, setPasswordValid }) => {
  const [hideXIcons, setHideXIcons] = useState(false);
  const [isReset, setIsReset] = useState(true);
  const [passwordActive, setPasswordActive] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const [values, setValues] = useState(null);

  const PASSWORD_TESTS = [
    {
      validator: ({ password: pwd }) => pwd.length >= 8,
      i18n: 'password_requirements_minimum_length', // include at least 8 characters'
    },
    {
      validator: ({ password: pwd }) => pwd.match(/\D/g),
      i18n: 'password_requirements_contains_letter', // include at least 1 letter'
    },
    {
      validator: ({ password: pwd }) => pwd.match(/\d/g),
      i18n: 'password_requirements_contains_number', // include at least 1 number'
    },
    {
      validator: ({ password: pwd }) => !pwd.match(/\s/g),
      i18n: 'password_requirements_no_spaces', // cannot contain spaces'
    },
    {
      validator: ({ password: pwd, ...fields }) =>
        Object.keys(fields).every((field) => !pwd.toLowerCase().match(fields[field].toLowerCase())),
      i18n: email ? 'password_requirements_name_or_email' : 'password_requirements_name', // cannot contain your name or email address
    },
  ];

  // every subscribed form change triggers this function
  const handleChange = useCallback(
    (formState) => {
      const { active, values: currentValues, visited } = formState;

      // the field has focus
      const isPasswordActive = active === password;
      const prevPasswordValue = values?.[password];
      const currentPasswordValue = currentValues?.[password];

      // default isReset to its previous value
      let nextStateReset = isReset;

      // only change isReset for these cases
      if (isPasswordActive && !currentPasswordValue && !!prevPasswordValue) {
        // user cleared the field
        nextStateReset = true;
      } else if (passwordActive && !isPasswordActive && !!currentPasswordValue) {
        // user blurred the field
        nextStateReset = false;
      }

      // if the password field ever received focus, show requirements
      const shouldShowRequirements = showRequirements || visited[password];

      // hide the X icons if: there is no value, the field was reset
      const shouldHideXIcons = !currentPasswordValue || nextStateReset;

      // remove untracked fields from form values to avoid false results
      const trackableFields = [password, firstName, lastName, email];
      const trackableValues = {};
      trackableFields.forEach((field) => {
        if (currentValues[field]) {
          trackableValues[field] = currentValues[field];
        }
      });

      // if submit failed and password exists, check if any validation failed to force an error on the form
      if (trackableValues.password) {
        const passwordValid = PASSWORD_TESTS.every(({ validator }) => validator(trackableValues));
        setPasswordValid(passwordValid);
      }

      // Update states
      setPasswordActive(isPasswordActive);
      setIsReset(nextStateReset);
      setShowRequirements(shouldShowRequirements);
      setHideXIcons(shouldHideXIcons);
      setValues(trackableValues);
    },
    [password, setPasswordValid, values, passwordActive, showRequirements, isReset]
  );

  const passwordValue = values?.[password];

  return (
    <>
      {showRequirements && (
        <div className='password-requirements'>
          <h6 className='password-requirements__heading'>{utils.i18n('password_requirements_heading')}</h6>
          <ul className='password-requirements__list'>
            {PASSWORD_TESTS.map(({ validator, i18n }) => {
              const passed = passwordValue && validator(values);
              const failed = !passed && !hideXIcons;
              const dashed = !passed && hideXIcons;
              let message = '';
              if (passed) {
                message = ANALYTICS.PASSED;
              }
              if (failed) {
                message = ANALYTICS.FAILED;
              }

              return (
                <li
                  className={cn('password-requirements__list-item', {
                    passed,
                    failed,
                    dashed,
                  })}
                  key={i18n}
                >
                  <VisHiddenText message={message} />
                  {utils.i18n(i18n)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* hack for handleChange to get called  before render, referred from here https://github.com/final-form/react-final-form/issues/809#issuecomment-808942373
       */}
      <FormSpy
        onChange={(formState) => setTimeout(() => handleChange(formState), 0)}
        subscription={{ active: true, values: true, visited: true, submitFailed: true }}
      />
    </>
  );
};

PasswordRequirements.propTypes = {
  password: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string, // not required
  setPasswordValid: PropTypes.func.isRequired,
};

/**
 * A function that is aware of "setPasswordValid"'s latest state (isPasswordValid), and returns a Final Form validation function.
 * The function is used to mark the Password field invalid, when Password Requirements are not met.
 * Apply this validation function to your form, or create your own custom version
 * 
 * example usage:
 * <Form
      onSubmit={this.handleSubmit}
      validate={customPasswordValidation(isPasswordValid)}
 */
export const customPasswordValidation = (isPasswordValid) => () => {
  const errors = {};

  if (!isPasswordValid) {
    errors.password = true;
  }

  return errors;
};

export default PasswordRequirements;
